import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Modal from '../../BaseComponents/Modal';
import ConfirmModal from '../../BaseComponents/Modal/ConfirmModal';
import Button from '../../BaseComponents/Button';
import {
  getMember,
  mergeMemberProfiles,
  searchMembersForMerge,
} from '../../../api/members';
import {
  GetMemberResponse,
  ListMembersResponseElement,
} from '../../../types/apiGeneratedTypes';
import './MergeProfilesModal.scss';

type MergeProfilesModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onMergeComplete?: () => void;
};

type MergeSlot = 'primary' | 'source';

const STEPS = ['Select profiles', 'Confirm merge'] as const;

const getApiErrorMessage = (err: unknown, fallback: string): string => {
  const error = err as {
    response?: { data?: { description?: string; detail?: string | Array<{ msg?: string }> } };
    message?: string;
  };
  const data = error?.response?.data;
  if (!data) return error?.message || fallback;
  if (data.description) return data.description;
  if (typeof data.detail === 'string') return data.detail;
  if (Array.isArray(data.detail)) {
    return data.detail.map((item) => item?.msg || String(item)).join(', ');
  }
  return error?.message || fallback;
};

const MergeProfilesModal: React.FC<MergeProfilesModalProps> = ({
  isOpen,
  onClose,
  onMergeComplete,
}) => {
  const [step, setStep] = useState(0);
  const [primary, setPrimary] = useState<GetMemberResponse | null>(null);
  const [source, setSource] = useState<GetMemberResponse | null>(null);

  const [primaryQuery, setPrimaryQuery] = useState('');
  const [sourceQuery, setSourceQuery] = useState('');
  const [primaryResults, setPrimaryResults] = useState<ListMembersResponseElement[]>([]);
  const [sourceResults, setSourceResults] = useState<ListMembersResponseElement[]>([]);
  const [primarySearching, setPrimarySearching] = useState(false);
  const [sourceSearching, setSourceSearching] = useState(false);
  const [primaryDropdownOpen, setPrimaryDropdownOpen] = useState(false);
  const [sourceDropdownOpen, setSourceDropdownOpen] = useState(false);
  const [loadingMember, setLoadingMember] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isConfirmPopupOpen, setIsConfirmPopupOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const primarySearchRef = useRef<HTMLDivElement>(null);
  const sourceSearchRef = useRef<HTMLDivElement>(null);
  const primaryDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sourceDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetState = useCallback(() => {
    setStep(0);
    setPrimary(null);
    setSource(null);
    setPrimaryQuery('');
    setSourceQuery('');
    setPrimaryResults([]);
    setSourceResults([]);
    setPrimaryDropdownOpen(false);
    setSourceDropdownOpen(false);
    setLoadingMember(false);
    setSubmitting(false);
    setIsConfirmPopupOpen(false);
    setError(null);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen, resetState]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (primarySearchRef.current && !primarySearchRef.current.contains(target)) {
        setPrimaryDropdownOpen(false);
      }
      if (sourceSearchRef.current && !sourceSearchRef.current.contains(target)) {
        setSourceDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const runMemberSearch = useCallback(
    async (query: string, slot: MergeSlot, excludeId?: string) => {
      const trimmed = query.trim();
      if (trimmed.length < 2) {
        if (slot === 'primary') {
          setPrimaryResults([]);
          setPrimarySearching(false);
        } else {
          setSourceResults([]);
          setSourceSearching(false);
        }
        return;
      }

      try {
        if (slot === 'primary') setPrimarySearching(true);
        else setSourceSearching(true);

        const response = await searchMembersForMerge(trimmed, 15);
        const filtered = (response.members || []).filter(
          (member) => member.id !== excludeId
        );

        if (slot === 'primary') setPrimaryResults(filtered);
        else setSourceResults(filtered);
      } catch {
        if (slot === 'primary') setPrimaryResults([]);
        else setSourceResults([]);
        toast.error('Failed to search members. Please try again.');
      } finally {
        if (slot === 'primary') setPrimarySearching(false);
        else setSourceSearching(false);
      }
    },
    []
  );

  const handleSearchChange = (slot: MergeSlot, value: string) => {
    if (slot === 'primary') {
      setPrimaryQuery(value);
      setPrimaryDropdownOpen(true);
      if (primaryDebounceRef.current) clearTimeout(primaryDebounceRef.current);
      primaryDebounceRef.current = setTimeout(() => {
        runMemberSearch(value, 'primary', source?.user_id);
      }, 350);
    } else {
      setSourceQuery(value);
      setSourceDropdownOpen(true);
      if (sourceDebounceRef.current) clearTimeout(sourceDebounceRef.current);
      sourceDebounceRef.current = setTimeout(() => {
        runMemberSearch(value, 'source', primary?.user_id);
      }, 350);
    }
  };

  const selectMember = async (slot: MergeSlot, memberId: string) => {
    if (slot === 'primary' && source?.user_id === memberId) {
      setError('Primary and old profiles must be different members.');
      return;
    }
    if (slot === 'source' && primary?.user_id === memberId) {
      setError('Primary and old profiles must be different members.');
      return;
    }

    try {
      setLoadingMember(true);
      setError(null);
      const member = await getMember(memberId);

      if (slot === 'primary') {
        setPrimary(member);
        setPrimaryQuery('');
        setPrimaryResults([]);
        setPrimaryDropdownOpen(false);
      } else {
        setSource(member);
        setSourceQuery('');
        setSourceResults([]);
        setSourceDropdownOpen(false);
      }
    } catch {
      setError('Could not load member details. Please try again.');
    } finally {
      setLoadingMember(false);
    }
  };

  const clearSlot = (slot: MergeSlot) => {
    if (slot === 'primary') {
      setPrimary(null);
      setPrimaryQuery('');
      setPrimaryResults([]);
    } else {
      setSource(null);
      setSourceQuery('');
      setSourceResults([]);
    }
    setError(null);
  };

  const canContinue = Boolean(primary && source);
  const canConfirm = canContinue && step === 1;

  const handleNext = () => {
    setError(null);
    if (!canContinue) {
      setError('Select both a primary profile and an old profile to continue.');
      return;
    }
    setStep(1);
  };

  const handleBack = () => {
    setError(null);
    setStep(0);
  };

  const handleConfirmMerge = async () => {
    if (!primary || !source) return;

    setSubmitting(true);
    setError(null);

    try {
      await mergeMemberProfiles({
        primary_member_id: primary.user_id,
        source_member_id: source.user_id,
      });
      setIsConfirmPopupOpen(false);
      toast.success(
        'Data was linked to the primary member and the old profile was deleted.'
      );
      onMergeComplete?.();
      onClose();
    } catch (err: unknown) {
      const message = getApiErrorMessage(
        err,
        'Failed to merge profiles. Please try again.'
      );
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenConfirmPopup = () => {
    if (!primary || !source) return;
    setError(null);
    setIsConfirmPopupOpen(true);
  };

  const renderProfileDetails = (member: GetMemberResponse) => (
    <div className='profile-details'>
      <div className='profile-name'>
        {member.first_name} {member.last_name}
      </div>
      <div className='profile-row'>
        <span className='label'>Email</span>
        <span className='value'>{member.email || '—'}</span>
      </div>
      <div className='profile-row'>
        <span className='label'>Plan</span>
        <span className='value'>{member.membership_type || '—'}</span>
      </div>
      <div className='profile-row'>
        <span className='label'>ID</span>
        <span className='value'>{member.unique_id || '—'}</span>
      </div>
    </div>
  );

  const renderSearchSlot = (slot: MergeSlot) => {
    const isPrimary = slot === 'primary';
    const selected = isPrimary ? primary : source;
    const query = isPrimary ? primaryQuery : sourceQuery;
    const results = isPrimary ? primaryResults : sourceResults;
    const searching = isPrimary ? primarySearching : sourceSearching;
    const dropdownOpen = isPrimary ? primaryDropdownOpen : sourceDropdownOpen;
    const searchRef = isPrimary ? primarySearchRef : sourceSearchRef;

    return (
      <div className={`merge-slot ${isPrimary ? 'primary' : 'duplicate'}`}>
        <div className='merge-slot-header'>
          <div className='slot-title'>
            {isPrimary
              ? 'Primary profile — keep this account'
              : 'Old profile — move data to primary'}
          </div>
        </div>

        {selected ? (
          <div className='selected-profile-card'>
            {renderProfileDetails(selected)}
            <button
              type='button'
              className='clear-selection'
              onClick={() => clearSlot(slot)}
            >
              Change
            </button>
          </div>
        ) : (
          <div className='merge-search' ref={searchRef}>
            <input
              className='search-input'
              type='search'
              value={query}
              placeholder='Search by name, email, or phone'
              disabled={loadingMember}
              autoComplete='off'
              autoCorrect='off'
              spellCheck={false}
              name={isPrimary ? 'merge-primary-search' : 'merge-source-search'}
              onChange={(e) => handleSearchChange(slot, e.target.value)}
              onFocus={() => {
                if (isPrimary) setPrimaryDropdownOpen(true);
                else setSourceDropdownOpen(true);
              }}
            />
            {dropdownOpen && query.trim().length >= 2 && (
              <div className='search-dropdown'>
                {searching || loadingMember ? (
                  <div className='search-loading'>Searching…</div>
                ) : results.length === 0 ? (
                  <div className='search-empty'>No members found</div>
                ) : (
                  results.map((member) => (
                    <button
                      key={member.id}
                      type='button'
                      className='search-option'
                      onClick={() => selectMember(slot, member.id)}
                    >
                      <span className='option-name'>
                        {member.first_name} {member.last_name}
                      </span>
                      <span className='option-meta'>
                        ID {member.unique_id}
                        {member.membership_type
                          ? ` · ${member.membership_type}`
                          : ''}
                        {member.is_active ? '' : ' · Disabled'}
                      </span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderConfirmStep = () => {
    if (!primary || !source) return null;

    return (
      <div className='confirm-summary'>
        <div className='confirm-warning'>
          Related data from the old profile (appointments, results, notes,
          alerts, etc.) will be linked to the primary member ID. The old profile
          will then be deleted.
        </div>

        <div className='confirm-cards'>
          <div className='confirm-card remove'>
            <span className='card-badge'>Move data from</span>
            {renderProfileDetails(source)}
          </div>
          <div className='confirm-arrow'>→</div>
          <div className='confirm-card keep'>
            <span className='card-badge'>Keep</span>
            {renderProfileDetails(primary)}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={submitting ? () => undefined : onClose}
      title='Merge duplicate profiles'
      width='760px'
      closeOnEsc={!submitting}
    >
      <div className='merge-profiles-modal'>
        <div className='merge-steps'>
          {STEPS.map((label, index) => (
            <div
              key={label}
              className={`merge-step-pill ${
                index === step ? 'active' : index < step ? 'done' : ''
              }`}
            >
              <span className='step-index'>{index + 1}</span>
              {label}
            </div>
          ))}
        </div>

        {step === 0 && (
          <div className='merge-columns'>
            {renderSearchSlot('primary')}
            {renderSearchSlot('source')}
          </div>
        )}

        {step === 1 && renderConfirmStep()}

        {error && !isConfirmPopupOpen && <p className='merge-error'>{error}</p>}

        <div className='merge-actions'>
          {step > 0 ? (
            <Button
              text='Back'
              onClick={handleBack}
              backgroundColor='#ffffff'
              color='#2D334E'
              width='120px'
              isDisabled={submitting}
            />
          ) : (
            <Button
              text='Cancel'
              onClick={onClose}
              backgroundColor='#ffffff'
              color='#2D334E'
              width='120px'
              isDisabled={submitting}
            />
          )}

          {step === 0 ? (
            <Button
              text='Continue'
              onClick={handleNext}
              width='140px'
              isDisabled={!canContinue}
            />
          ) : (
            <Button
              text='Confirm merge'
              onClick={handleOpenConfirmPopup}
              width='160px'
              isDisabled={!canConfirm || submitting}
            />
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={isConfirmPopupOpen}
        onClose={() => {
          if (!submitting) setIsConfirmPopupOpen(false);
        }}
        onConfirmClick={handleConfirmMerge}
        title='Are you sure you want to merge these profiles?'
        subtitle={
          primary && source
            ? `Data from ${source.first_name} ${source.last_name} will be linked to ${primary.first_name} ${primary.last_name}, then the old profile will be deleted.`
            : 'Related data from the old profile will be linked to the primary member and the old profile will be deleted.'
        }
        cancelBtnText='Cancel'
        confirmBtnText='Yes, merge'
        loading={submitting}
        error={isConfirmPopupOpen ? error ?? undefined : undefined}
      />
    </Modal>
  );
};

export default MergeProfilesModal;
